import { useEffect, useMemo, useState } from 'react';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconAnimals, IconClose } from '../../../common/icons';
import { useCombobox } from '../../../common/hooks/use-combobox';
import { useAnimalsList } from '../contexts/animals';

// Reuses the typeahead styles from the clients searcher.
import '../../clients/styles/search.css';

// Animal typeahead, controlled by `value` (the selected animal). `sex` scopes the
// search (default 'female' = donante; 'male' = toro). value set → read-only chip +
// X to clear; value null → live server search (q over name/number/register).
export default function AnimalDonorSearch({ value, onSelect, onClear, autoFocus = false, sex = 'female', placeholder, emptyText }) {
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');

    const ph = placeholder || (sex === 'male' ? 'Buscar toro por nombre o registro…' : 'Buscar vaca por nombre o número…');
    const empty = emptyText || (sex === 'male' ? 'Sin toros.' : 'Sin vacas.');

    useEffect(() => {
        const t = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [text]);

    const filters = useMemo(() => {
        const out = { sex, limit: 15 };
        if (debounced.trim()) out.q = debounced.trim();
        return out;
    }, [debounced, sex]);

    const { data, isFetching } = useAnimalsList(value ? { sex, limit: 1 } : filters);
    const animals = value ? [] : (data?.items ?? []);

    const choose = (a) => { setText(''); onSelect?.(a); };
    const cb = useCombobox({ items: animals, onChoose: choose });

    if (value) {
        const tail = value.number || value.register;
        return (
            <div className="search">
                <div className="search__field search__field--selected">
                    <span className="search__field-icon"><IconAnimals /></span>
                    <span className="search__selected-text">{value.name}{tail ? ` · ${tail}` : ''}</span>
                    <button type="button" className="search__clear" onClick={() => { setText(''); onClear?.(); }} aria-label="Cambiar" title="Cambiar">
                        <IconClose />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="search">
            <input
                type="text"
                className="search__input"
                value={text}
                placeholder={ph}
                autoFocus={autoFocus}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cb.open}
                aria-controls={cb.listId}
                aria-activedescendant={cb.activeId}
                onChange={(e) => { setText(e.target.value); cb.setOpen(true); }}
                onFocus={cb.onFocus}
                onBlur={cb.onBlur}
                onKeyDown={cb.onKeyDown}
            />
            {cb.open && (
                <div className="search__panel" role="listbox" id={cb.listId}>
                    {!animals.length ? (
                        <p className="search__hint">{isFetching ? 'Buscando…' : empty}</p>
                    ) : (
                        animals.map((a, i) => (
                            <button
                                key={a.id}
                                type="button"
                                id={cb.optionId(i)}
                                role="option"
                                aria-selected={cb.activeIndex === i}
                                className={`search__item ${cb.activeIndex === i ? 'search__item--active' : ''}`}
                                onMouseDown={cb.onOptionMouseDown(a)}
                                onMouseEnter={() => cb.setActive(i)}
                            >
                                <span className="search__item-icon"><IconAnimals /></span>
                                <span className="search__item-body">
                                    <span className="search__item-name">{a.name}</span>
                                    <span className="search__item-sub">
                                        {[a.number && `Nº ${a.number}`, a.register, a.race?.name].filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
